import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Amount, ConfirmModal, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError, Textarea } from '@redpanda/ui';
import { formatTableDate } from '@redpanda/utils';
import { setProjectBadDebt, clearProjectBadDebt } from '../services/api';
import { notifyDataChanged } from '../utils/dataEvents';
import { PMProject } from '../types/pm';

interface BadDebtControlProps {
  project: PMProject;
  /** 管理員才能列入／取消（影響財務數字）；一般成員只看得到標示 */
  canEdit: boolean;
  onChange: (project: PMProject) => void;
}

/**
 * 專案詳情財務區的呆帳標示（照會計的沖銷做法：不刪合約與收款，剩餘未收改計入呆帳）
 * - 已列入：紅字顯示呆帳金額、日期、原因，管理員可取消
 * - 未列入：還有待收款時，管理員可「列為呆帳」
 */
const BadDebtControl: React.FC<BadDebtControlProps> = ({ project, canEdit, onChange }) => {
  const { t, i18n } = useTranslation(['translation', 'pages']);
  const [dialog, setDialog] = useState<'mark' | 'clear' | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (action: 'mark' | 'clear') => {
    setSaving(true);
    try {
      const response = action === 'mark' ? await setProjectBadDebt(project.id, note.trim() || null) : await clearProjectBadDebt(project.id);
      onChange({ ...project, ...response.data });
      toast.success(t(action === 'mark' ? 'pmProjects.badDebt.marked' : 'pmProjects.badDebt.cleared', { ns: 'pages' }));
      notifyDataChanged('projects'); // 儀表板待收款、應收帳款跟著更新
      setDialog(null);
      setNote('');
    } catch (error: any) {
      console.error('Failed to update bad debt:', error);
      toast.error(error.response?.data?.error || t('common.messages.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (project.badDebtAt) {
    return (
      <div className="mt-4 pt-4 border-t border-line flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-sm">
        <div className="min-w-0">
          <span className="font-medium text-red-600 dark:text-red-400">
            {t('pmProjects.badDebt.label', { ns: 'pages' })} <Amount value={project.badDebtAmount ?? 0} />
          </span>
          <span className="ml-2 text-fg-secondary">
            {t('pmProjects.badDebt.since', { ns: 'pages', date: formatTableDate(project.badDebtAt, i18n.language) })}
            {project.badDebtNote && <>・{project.badDebtNote}</>}
          </span>
        </div>
        {canEdit && (
          <button type="button" onClick={() => setDialog('clear')} className="text-sm text-accent hover:underline">
            {t('pmProjects.badDebt.clear', { ns: 'pages' })}
          </button>
        )}
        <ConfirmModal
          isOpen={dialog === 'clear'}
          onClose={() => setDialog(null)}
          onConfirm={() => submit('clear')}
          title={t('pmProjects.badDebt.clear', { ns: 'pages' })}
          message={t('pmProjects.badDebt.clearConfirm', { ns: 'pages' })}
          isLoading={saving}
          t={t}
        />
      </div>
    );
  }

  if (!canEdit || (project.pendingAmount ?? 0) <= 0) return null;

  return (
    <>
      <div className="mt-3 text-right">
        <button type="button" onClick={() => setDialog('mark')} className="text-sm text-fg-secondary hover:text-red-600 dark:hover:text-red-400 hover:underline">
          {t('pmProjects.badDebt.mark', { ns: 'pages' })}
        </button>
      </div>
      {dialog === 'mark' && (
        <DialogShell onClose={() => setDialog(null)} closeDisabled={saving} className="max-w-lg">
          <DialogHeader title={t('pmProjects.badDebt.mark', { ns: 'pages' })} onClose={() => setDialog(null)} closeDisabled={saving} closeLabel={t('common.actions.close')} />
          <div className="p-6 space-y-5">
            <p className="text-sm text-fg-secondary">
              {t('pmProjects.badDebt.message', { ns: 'pages', amount: `$${(project.pendingAmount ?? 0).toLocaleString()}` })}
            </p>
            <FormFieldWithError label={t('pmProjects.badDebt.noteLabel', { ns: 'pages' })} htmlFor="badDebtNote">
              <Textarea
                id="badDebtNote"
                rows={3}
                maxLength={500}
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder={t('pmProjects.badDebt.notePlaceholder', { ns: 'pages' })}
              />
            </FormFieldWithError>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setDialog(null)} disabled={saving} className={dialogButtonClass.cancel}>
              {t('common.actions.cancel')}
            </button>
            <button type="button" onClick={() => submit('mark')} disabled={saving} className={dialogButtonClass.danger}>
              {saving ? t('common.status.saving') : t('pmProjects.badDebt.confirm', { ns: 'pages' })}
            </button>
          </DialogFooter>
        </DialogShell>
      )}
    </>
  );
};

export default BadDebtControl;
